"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, type ReactNode, useContext, useEffect, useState } from "react";
import { type OfficeReadyInfo, waitForOfficeReady } from "@/lib/office";
type OfficeContextValue = {
  isReady: boolean;
  host: string | null;
  platform: string | null;
};
const OfficeContext = createContext<OfficeContextValue>({
  isReady: false,
  host: null,
  platform: null
});
export function useOffice() {
  return useContext(OfficeContext);
}

/**
 * Provides Office.js readiness state to descendant components.
 *
 * `isReady` only flips to true after BOTH:
 *   - the `Office` global has been attached by office.js, AND
 *   - `Office.onReady()` has resolved (so the host bridge is fully
 *     initialized).
 *
 * MSAL's NAA bridge (`createNestablePublicClientApplication`) uses
 * Office APIs internally; calling it before that bridge is up throws
 * "Office.js has not fully loaded". Gating downstream consumers on
 * `isReady` keeps that ordering correct.
 */
export function OfficeProvider(t0) {
  const $ = _c(6);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      isReady: false,
      host: null,
      platform: null
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [state, setState] = useState(t1);
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      let cancelled = false;
      waitForOfficeReady().then(info => {
        if (cancelled) {
          return;
        }
        if (info.status === "timeout") {
          console.warn("[Magnus Add-in] Office.js did not initialize; staying in loading state.");
          return;
        }
        setState({
          isReady: true,
          host: info.host,
          platform: info.platform
        });
      });
      return () => {
        cancelled = true;
      };
    };
    t3 = [];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  if ($[3] !== children || $[4] !== state) {
    t4 = <OfficeContext.Provider value={state}>{children}</OfficeContext.Provider>;
    $[3] = children;
    $[4] = state;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}